/**
 * @fileoverview Componente responsável por exibir e controlar o token de API da conta.
 */

import React, { useState } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import WarningIcon from "@material-ui/icons/Warning";
import { green } from "@material-ui/core/colors";

import api from "../../services/api";
import toastError from "../../errors/toastError";

import { Eye, Copy, EyeOff } from "lucide-react";
const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: '24px',
      background: theme.palette.type === 'dark'
        ? 'linear-gradient(135deg, rgba(18, 18, 19, 0.95) 0%, rgba(22, 22, 24, 0.95) 100%)'
        : '#FFFFFF',
      boxShadow: theme.palette.type === 'dark'
        ? '0 24px 80px rgba(0, 0, 0, 0.6)'
        : '0 24px 80px rgba(0, 0, 0, 0.12)',
      border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.2)' : 'none',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '3px',
        background: theme.palette.type === 'dark'
          ? 'linear-gradient(90deg, #5B7CFF 0%, #9D7CFF 100%)'
          : 'linear-gradient(90deg, #3D5CFD 0%, #7C3AED 100%)',
        borderRadius: '24px 24px 0 0',
      },
    },
    '& .MuiBackdrop-root': {
      backgroundColor: theme.palette.type === 'dark' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.4)',
    },
  },
  dialogTitle: {
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    borderBottom: theme.palette.type === 'dark'
      ? '1px solid rgba(91, 124, 255, 0.15)'
      : '1px solid rgba(0, 0, 0, 0.08)',
    '& .MuiTypography-root': {
      fontSize: '1.5rem',
      fontWeight: 700,
    },
  },
  textField: {
    marginTop: theme.spacing(2),
    '& .MuiOutlinedInput-root': {
      borderRadius: '14px',
      background: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
      border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.15)' : '1px solid rgba(0, 0, 0, 0.08)',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '& fieldset': {
        borderColor: 'transparent',
      },
      '&:hover': {
        background: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(61, 92, 253, 0.04)',
      },
      '&.Mui-focused': {
        background: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 1)',
        border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.4)' : '1px solid rgba(61, 92, 253, 0.3)',
        boxShadow: '0 0 0 4px rgba(91, 124, 255, 0.15)',
      },
    },
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  tokenDisplay: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    borderRadius: '12px',
    background: theme.palette.type === 'dark'
      ? 'rgba(34, 197, 94, 0.1)'
      : 'rgba(34, 197, 94, 0.05)',
    border: theme.palette.type === 'dark'
      ? '1px solid rgba(34, 197, 94, 0.3)'
      : '1px solid rgba(34, 197, 94, 0.2)',
  },
  tokenValue: {
    fontFamily: 'monospace',
    fontSize: '0.9rem',
    wordBreak: 'break-all',
    padding: theme.spacing(1),
    borderRadius: '8px',
    background: theme.palette.type === 'dark'
      ? 'rgba(0, 0, 0, 0.3)'
      : 'rgba(0, 0, 0, 0.05)',
  },
  warningBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: '12px',
    background: theme.palette.type === 'dark'
      ? 'rgba(234, 179, 8, 0.1)'
      : 'rgba(234, 179, 8, 0.05)',
    border: theme.palette.type === 'dark'
      ? '1px solid rgba(234, 179, 8, 0.3)'
      : '1px solid rgba(234, 179, 8, 0.2)',
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  warningIcon: {
    color: '#eab308',
  },
}));

const ApiTokenModal = ({ open, onClose }) => {
  const classes = useStyles();
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [generatedToken, setGeneratedToken] = useState(null);
  const [showToken, setShowToken] = useState(false);

  const handleClose = () => {
    setName("");
    setGeneratedToken(null);
    setShowToken(false);
    onClose(generatedToken);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Digite um nome para o token");
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.post("/api-tokens", { name: name.trim() });
      setGeneratedToken(data.token);
      toast.success("Token criado com sucesso!");
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyToken = () => {
    if (generatedToken) {
      navigator.clipboard.writeText(generatedToken);
      toast.success("Token copiado para a área de transferência!");
    }
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        className={classes.dialog}
      >
        <DialogTitle className={classes.dialogTitle}>
          {generatedToken ? "Token Criado" : "Novo Token de API"}
        </DialogTitle>
        <DialogContent dividers>
          {!generatedToken ? (
            <form onSubmit={handleSubmit}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Crie um token de API para integrar com N8N ou outras plataformas externas.
                O token será exibido apenas uma vez após a criação.
              </Typography>
              <TextField
                label="Nome do Token"
                placeholder="Ex: Integração N8N WordPress"
                value={name}
                onChange={(e) => setName(e.target.value)}
                variant="outlined"
                fullWidth
                className={classes.textField}
                autoFocus
              />
            </form>
          ) : (
            <>
              <Typography variant="body1" gutterBottom style={{ fontWeight: 600, color: '#22c55e' }}>
                Token gerado com sucesso!
              </Typography>

              <Box className={classes.tokenDisplay}>
                <Typography variant="body2" gutterBottom>
                  Seu token de API:
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography className={classes.tokenValue} style={{ flex: 1 }}>
                    {showToken ? generatedToken : '•'.repeat(64)}
                  </Typography>
                  <IconButton size="small" onClick={() => setShowToken(!showToken)}>
                    {showToken ? <EyeOff /> : <Eye />}
                  </IconButton>
                  <IconButton size="small" onClick={handleCopyToken}>
                    <Copy />
                  </IconButton>
                </Box>
              </Box>

              <Box className={classes.warningBox}>
                <WarningIcon className={classes.warningIcon} />
                <Box>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    Atenção!
                  </Typography>
                  <Typography variant="body2">
                    Copie e guarde este token agora. Por segurança, ele não será exibido novamente.
                    Se você perder o token, precisará criar um novo.
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {!generatedToken ? (
            <>
              <Button
                onClick={handleClose}
                color="secondary"
                variant="outlined"
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSubmit}
                color="primary"
                variant="contained"
                disabled={loading || !name.trim()}
                className={classes.btnWrapper}
              >
                Criar Token
                {loading && (
                  <CircularProgress
                    size={24}
                    className={classes.buttonProgress}
                  />
                )}
              </Button>
            </>
          ) : (
            <Button
              onClick={handleClose}
              color="primary"
              variant="contained"
            >
              Fechar
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ApiTokenModal;
